/**
 * This is composable to check user permission and roles.
 * @example hasRole([RoleEnum.ADMIN])
 * @example hasPermission([PermissionEnum.VIEW_ASSET])
 */
import type { ComputedRef } from 'vue'

import filter from 'lodash-es/filter'
import intersection from 'lodash-es/intersection'
import isArray from 'lodash-es/isArray'
import isFunction from 'lodash-es/isFunction'
import map from 'lodash-es/map'

import { MANAGER_ROLES } from '/@/constants/user'
import { OrganizationEarlyAccessFeatureEnum } from '/@/enums/organizationEnum'
import { PermissionEnum, RoleEnum } from '/@/enums/userEnum'
import { checkRole } from '/@/utils/permissions'

/**
 * UsePermission is a store related permission operations.
 */
export function useUserPermission() {
  const accountStore = useAccountStore()
  const organizationStore = useOrganizationStore()
  const router = useRouter()

  /**
   * Determine user's authority by role.
   * @param [value=ADMIN] - UserRole
   */
  function hasRole(value?: RoleEnum | RoleEnum[] | string | string[]): boolean {
    const { user } = accountStore
    // It means multiple role can do that.
    return checkRole(user?.role as RoleEnum, value)
  }

  // These variables are used to combine where roles are common.
  /* Getters */
  const isAnyManager: ComputedRef<boolean> = computed(() => hasRole(MANAGER_ROLES))

  const isAdmin: ComputedRef<boolean> = computed(() => hasRole(RoleEnum.ADMIN))

  const accountManagerRestrictedRoles: string[] = filter(
    Object.values(RoleEnum),
    (value) => [RoleEnum.ADMIN, RoleEnum.ANALYST_USER].includes(value),
  )

  const isAccountManager: ComputedRef<boolean> = computed(() =>
    hasRole(RoleEnum.ACCOUNT_MANAGER),
  )

  // Normal perms
  const isAnyNormalUser: ComputedRef<boolean> = computed(() =>
    hasRole([RoleEnum.ORGANIZATION_USER, RoleEnum.ORGANIZATION_MANAGER]),
  )

  const isOrganizationManager: ComputedRef<boolean> = computed(() =>
    hasRole(RoleEnum.ORGANIZATION_MANAGER),
  )

  const isNormalManager: ComputedRef<boolean> = computed(() =>
    hasRole([RoleEnum.MSSP_MANAGER, RoleEnum.ORGANIZATION_MANAGER]),
  )

  // Mssp perms
  const isAnyMsspUser: ComputedRef<boolean> = computed(() =>
    hasRole([RoleEnum.MSSP_MANAGER, RoleEnum.MSSP_USER]),
  )
  const isMsspManager: ComputedRef<boolean> = computed(() =>
    hasRole(RoleEnum.MSSP_MANAGER),
  )
  const isMsspUser: ComputedRef<boolean> = computed(() => hasRole(RoleEnum.MSSP_USER))

  const isOrganizationUser: ComputedRef<boolean> = computed(() =>
    hasRole(RoleEnum.ORGANIZATION_USER),
  )

  // Analyst perms
  const isAnalyst: ComputedRef<boolean> = computed(() => hasRole([RoleEnum.ANALYST_USER]))

  const isAnyAnalystUser: ComputedRef<boolean> = computed(() =>
    hasRole([RoleEnum.ANALYST_USER, RoleEnum.ADMIN, RoleEnum.ACCOUNT_MANAGER]),
  )

  const isAnyAnalystManager: ComputedRef<boolean> = computed(() =>
    hasRole([RoleEnum.ADMIN, RoleEnum.ACCOUNT_MANAGER]),
  )

  const canManageOtherOrganization: ComputedRef<boolean> = computed(
    () => isAnyAnalystUser.value || isAnyMsspUser.value,
  )

  const isCustomer: ComputedRef<boolean> = computed(() =>
    hasRole([
      RoleEnum.ORGANIZATION_MANAGER,
      RoleEnum.ORGANIZATION_USER,
      RoleEnum.MSSP_MANAGER,
      RoleEnum.MSSP_USER,
    ]),
  )

  /**
   * Case 1: (User role is Analyst or Admin)
   *    When enters the page, firstly show the state message.
   *    Must switch the organization to view content.
   *    Unselect organization, show state message.
   * Case 2: (User role is Mssp or Organization)
   *    When enters the page, firstly show content for itself (The organization can only see its own content.).
   *    When switch organization (organization can't), show content for switched organization.
   *    Unselect organization (organization can't), show content for itself.
   */
  const isDashboardVisible: ComputedRef<boolean | number | undefined> = computed(() => {
    // If user is analyst or admin and not switch organization, show state message
    if (canManageOtherOrganization.value && !organizationStore.organizationId)
      return false

    // If user is analyst or admin and switched organization, or user is mssp and switched organization, show dashboard
    return organizationStore.organizationId
  })

  const isDashboardVisibleForMsspUsers: ComputedRef<boolean | number | undefined> =
    computed(() => {
      if (isAnyMsspUser.value) return true
      // If user is analyst or admin and not switch organization, show state message
      if (canManageOtherOrganization.value && !organizationStore.organizationId)
        return false

      // If user is analyst or admin and switched organization, or user is mssp and switched organization, show dashboard
      return organizationStore.organizationId
    })

  /* Getters */

  /**
   * Determine user's authority by permission.
   * @param [value=view_asset] - Permission
   */
  function hasPermission(
    value?: PermissionEnum | PermissionEnum[] | string | string[],
  ): boolean {
    if (!value) {
      return true
    }
    const { user } = accountStore
    // Manager users already have permissions.
    // Because we do not store permissions for managers.
    if (isAnyManager.value) return true

    if (!isArray(value)) {
      return user?.permissions.includes(value as PermissionEnum) as boolean
    }
    // It means multiple permission can do this operation.
    return (intersection(value, user?.permissions) as PermissionEnum[]).length > 0
  }

  /**
   * Determine user's authority by modules.
   * IMPORTANT: Use module short code.
   * @param [value=breach-monitoring] - Module
   */
  function hasModule(value?: string | string[]) {
    // admin already have all modules.
    if (isAnyAnalystManager.value) return true
    const { user } = accountStore
    const moduleCodes = map(user?.modules, 'short_code')

    if (!isArray(value)) {
      return moduleCodes.includes(value as string) as boolean
    }
    // It means multiple modules can do this operation.
    return (intersection(value, moduleCodes) as string[]).length > 0
  }

  /**
   * @method hasEarlyAccessFeature
   * @description Determine user's authority by early access features.
   * IMPORTANT: Use OrganizationEarlyAccessFeatureEnum.
   * @example hasEarlyAccessFeature(OrganizationEarlyAccessFeatureEnum.ASM_REDESIGN)
   * This function checks if the user has access to early access features and
   * always returns false for all analyst users.
   * @param value
   */
  function hasEarlyAccessFeature(value: OrganizationEarlyAccessFeatureEnum): boolean {
    if (isAnyAnalystManager.value) return true

    const { user } = accountStore
    const earlyAccessFeatures = user?.organization?.early_access_features || []
    return earlyAccessFeatures.includes(value)
  }

  /**
   * Determine user's authority by module categories.
   * IMPORTANT: Use module short code.
   * @param value
   */
  function hasModuleCategory(value?: string | string[]) {
    if (isAnyAnalystManager.value) return true
    const { user } = accountStore

    const categoryCodes = map(user?.modules, 'category')

    if (!isArray(value)) return categoryCodes.includes(value)

    return (intersection(value, categoryCodes) as string[]).length > 0
  }

  /**
   * Decides whether to show based on roles
   * @param roles
   */
  function isShowedByRoles(roles?: Function | Array<string>): boolean {
    // If roles is not defined, show the column
    if (roles === undefined) return true
    // If roles is a function, call it and return the result
    if (isFunction(roles)) return roles()
    // If roles is an array, check if the user has any of the roles
    if (isArray(roles)) return hasRole(roles)
    return false
  }

  /**
   * @method isOwner
   * @param id {number} Holds id number.
   * @description Does check for owner.
   * @return boolean
   */
  function isOwner(id: number): boolean {
    const { user } = accountStore
    return user?.id === id
  }

  /**
   * @method checkDetailPageOwnership
   * @param redirectRoute {RouteLocationRaw} Desired redirect route
   * @description This provide control ownership for detail page by organization
   * @return void
   */
  async function checkDetailPageOwnership(redirectRoute: any) {
    await router.push(redirectRoute)
  }

  return {
    isAdmin,
    isOwner,
    isAnalyst,
    isAnyMsspUser,
    isAnyManager,
    isAccountManager,
    isMsspManager,
    isMsspUser,
    isOrganizationUser,
    isAnyNormalUser,
    isAnyAnalystUser,
    isDashboardVisible,
    isDashboardVisibleForMsspUsers,
    isAnyAnalystManager,
    isOrganizationManager,
    canManageOtherOrganization,
    isNormalManager,
    accountManagerRestrictedRoles,
    isCustomer,
    hasRole,
    hasModule,
    hasPermission,
    isShowedByRoles,
    hasModuleCategory,
    checkDetailPageOwnership,
    hasEarlyAccessFeature,
  }
}
